/* eslint-disable @typescript-eslint/no-unused-vars -- En rial, esta pantalla es de solo lectura */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ComunicacionAjax } from "../../../ts/api/comunicacion";
import "./Empaquetado.scss";
import { useLocation } from "react-router";
import { formatIDArt } from "../../../ts/util";
import { useCallback, useEffect, useRef, useState } from "react";
import { Loading, QueryError } from "../../../components/Loading/Loading";
import { Breadcrumb } from "../../../components/Breadcrumb";

export function EmpaquetadoPage() {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const idPedido = params.get("idPedido");
	if (idPedido == null) throw new Error("No se ha especificado un idPedido en la URL");
	const { data, error, isPending } = useSavedEstructura(idPedido);

	const [dirtyEstructura, setDirtyEstructura] = useState(data);
	useEffect(() => {
		console.log("Loaded estructura:", data);
		setDirtyEstructura(data);
	}, [data]);

	const onChildUpdate = useCallback((dropped: ItemEstructura, droppedFrom: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; }, droppedInto: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; }) => {
		if (dirtyEstructura == null) return;

		//#region Validaciones
		if (dropped.tipo == "articulo" && droppedInto.tipo == "top") {
			alert("Los articulos no pueden estar sueltos en el albaran, deben ir dentro de un bulto o pallet.");
			return;
		}
		if (droppedFrom.tipo == droppedInto.tipo && droppedFrom.id === droppedInto.id) {
			console.log("Dropped into the same container, no changes made.");
			return;
		}
		if (dropped.tipo == "pallet" && droppedInto.id != null) {
			alert("Los pallets no pueden ir dentro de otros elementos.");
			return;
		}
		if (dropped.tipo == droppedInto.tipo && dropped.id === droppedInto.id) {
			// Lo hemos soltado sobre nosotros mismos, me suena a un cancelado
			return;
		}
		//#endregion

		const newEstructura = { ...dirtyEstructura };

		if (droppedFrom.id == null) {
			newEstructura.estructura = newEstructura.estructura.filter(item => item.id !== dropped.id || item.tipo !== dropped.tipo);
		} else {
			const previousParent = findItemEstructura(droppedFrom.id, droppedFrom.tipo, newEstructura.estructura);
			if (previousParent) {
				previousParent.contenido = previousParent.contenido.filter(item => item.id !== dropped.id || item.tipo !== dropped.tipo);
			}
		}

		if (droppedInto.id == null) {
			newEstructura.estructura = [...newEstructura.estructura, dropped];
		} else {
			const newParent = findItemEstructura(droppedInto.id, droppedInto.tipo, newEstructura.estructura);
			if (newParent) {
				newParent.contenido = [...newParent.contenido, dropped];
			}
		}

		setDirtyEstructura(newEstructura);
	}, [dirtyEstructura]);


	if (error) return <QueryError error={error} fallback="Error al cargar la estructura guardada" />;
	if (isPending) return <Loading />;

	return (
		<div style={{ display: "flex", flex: 1 }}>
			<div id="contenedor">
				<Breadcrumb
					sectionTitle="Pedidos Venta"
					links={[
						{ label: "Pedidos Venta", to: "/cycles" },
						{ label: "Empaquetado " + idPedido, active: true },
					]}
				/>

				<div id="albaran" className="list">

					<AlbaranPaquete
						dirtyEstructura={dirtyEstructura}
						onChildUpdate={onChildUpdate}
					/>

				</div>
			</div>
			<SidebarPorUbicar estructura={dirtyEstructura} onChildUpdate={onChildUpdate} />
		</div>
	);
}




function AlbaranPaquete(props: { readonly dirtyEstructura: Estructura | undefined; readonly onChildUpdate: OnChildUpdate; }) {
	const { onChildUpdate } = props;
	const palletsEnAlbaran = props.dirtyEstructura?.estructura.filter(item => item.tipo === "pallet") ?? [];
	const bultosEnAlbaran = props.dirtyEstructura?.estructura.filter(item => item.tipo === "bulto") ?? [];

	return (
		<div
			id="albaranContainer"
		>
			{palletsEnAlbaran.map((pallet) => <ItemPallet key={pallet.codigo} data={pallet} parent={{ tipo: "top" }} onChange={onChildUpdate} />)}
			{bultosEnAlbaran.map((bulto) => <ItemBulto key={bulto.codigo} data={bulto} parent={{ tipo: "top" }} onChange={onChildUpdate} />)}
		</div>
	);
}


interface ItemArticuloProps<T> {
	readonly data: T;
	readonly parent: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; };
}
interface ItemContenedorProps<T> extends ItemArticuloProps<T> {
	readonly onChange: OnChildUpdate;
}
function ItemArticulo(props: ItemArticuloProps<ItemEstructuraArt>) {
	const { data, parent } = props;

	return (
		<div className="list-item articulo">
			<div>{formatIDArt(data.idArticulo, 0)}</div>
			<div>{data.descripcion}</div>
			<div>{data.cantidad} unidades</div>
		</div>
	);
}
function ItemBulto(props: ItemContenedorProps<ItemEstructuraBul>) {
	const { data, parent, onChange } = props;
	const contenido = props.data.contenido;
	const bultosInside = contenido.filter(item => item.tipo === "bulto");
	const articulosInside = contenido.filter(item => item.tipo === "articulo");

	return (
		<div className="list bulto">
			<div className="headerContenedor">
				<h3>Bulto</h3>
				{/* <input type="number" className="inputEstructura" value={props.data.codigo} onChange={(e) => props.onChange({ ...props.data, codigo: Number(e.target.value) })} /> */}
			</div>
			{articulosInside.map((art) => <ItemArticulo key={art.idLineaPedido} data={art} parent={props.data} />)}
			{bultosInside.map((bul) => <ItemBulto key={bul.codigo} data={bul} parent={props.data} onChange={props.onChange} />)}
		</div>
	);
}
function ItemPallet(props: ItemContenedorProps<ItemEstructuraPallet>) {
	const { data, parent, onChange } = props;
	const contenido = data.contenido;
	const bultosInside = contenido.filter(item => item.tipo === "bulto");
	const articulosInside = contenido.filter(item => item.tipo === "articulo");

	return (
		<div className="list pallet">
			<div className="headerContenedor">
				<h3>Pallet</h3>
				{/* <input type="number" className="inputEstructura" value={props.data.codigo} onChange={(e) => props.onChange({ ...props.data, codigo: Number(e.target.value) })} /> */}
			</div>
			{articulosInside.map((art) => <ItemArticulo key={art.idLineaPedido} data={art} parent={props.data} />)}
			{bultosInside.map((bul) => <ItemBulto key={bul.codigo} data={bul} parent={props.data} onChange={props.onChange} />)}
		</div>
	);
}


function SidebarPorUbicar(props: { readonly estructura: Estructura | undefined; readonly onChildUpdate: OnChildUpdate; }) {
	const { onChildUpdate } = props;
	const contenido = props.estructura?.estructura ?? [];

	const articulosSueltos = contenido.filter(item => item.tipo === "articulo");

	return (
		<div
			className="sidebar"
		>
			<h3>Articulos pendientes</h3>
			<div id="articulosSinUbicar">
				{articulosSueltos.map(art => <ItemArticulo key={art.idLineaPedido} data={art} parent={{ tipo: "sinUbicar" }} />)}
			</div>
		</div>
	);
}


function useSavedEstructura(idPedidoVenta: string) {
	return useQuery({
		queryKey: ["getEstructuraPedidoVenta", idPedidoVenta],
		queryFn: () => ComunicacionAjax.sendAjaxRequest<Estructura>("POST", "/api/v1/getEstructuraPedidoVenta", { idPedidoVenta }).then(response => {
			assignIds(response.estructura, -1);
			return response;
		}),
	});
}
interface Estructura {
	idEstructura: number,
	estructura: ItemEstructura[];
}
type ItemEstructura = ItemEstructuraArt | ItemEstructuraBul | ItemEstructuraPallet;
interface ItemEstructuraArt {
	tipo: "articulo";
	id: number;

	idPedido: string;
	idLineaPedido: string;

	idArticulo: string;
	descripcion: string;
	cantidad: number;
	cantidadEmpaquetada: number;
}
interface ItemEstructuraBul {
	tipo: "bulto";
	id: number;

	codigo: number;
	contenido: ItemEstructura[];
}
interface ItemEstructuraPallet {
	tipo: "pallet";
	id: number;

	codigo: number;
	contenido: ItemEstructura[];
}


function findItemEstructura(id: number | string, tipo: "bulto" | "pallet", estructura: ItemEstructura[]): ItemEstructuraBul | ItemEstructuraPallet | null {
	for (const item of estructura) {
		if (item.id === id && item.tipo === tipo) return item;
		if (item.tipo === "bulto" || item.tipo === "pallet") {
			const found = findItemEstructura(id, tipo, item.contenido);
			if (found) return found;
		}
	}
	return null;
}
function assignIds(estructura: ItemEstructura[], startFrom: number) {
	let currentId = startFrom;
	for (const item of estructura) {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Es para lo que usamos la función xd
		if (item.id != undefined && item.id > 0) continue; // Ya tiene ID asignado

		item.id = currentId--;
		if (item.tipo === "bulto" || item.tipo === "pallet") {
			assignIds(item.contenido, currentId);
		}
	}
}

type OnChildUpdate = (dropped: ItemEstructura, droppedFrom: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; }, droppedInto: ItemEstructuraBul | ItemEstructuraPallet | { tipo: "top" | "sinUbicar"; id?: null; }) => void;